import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { SurveyContext } from "../context/SurveyContext";
import Message from "./Message";
import { FaUserPlus, FaUserMinus, FaCalendarAlt, FaSpinner } from "react-icons/fa";
import UserListModal from "./UserListModal";
import { getProfilePicture } from "../utils/avatar";

const Profile = () => {
  const { username } = useParams();
  const { user } = useContext(AuthContext);
  const { condition } = useContext(SurveyContext);
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replies, setReplies] = useState([]);
  const [likes, setLikes] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [error, setError] = useState("");
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  const [activeTab, setActiveTab] = useState("posts");

  // Parse @mentions and #hashtags in text, making them clickable
  const renderTextWithLinks = (text) => {
    if (!text) return text;

    const parts = text.split(/(#[a-zA-Z0-9_]+|@[a-zA-Z0-9_\.]+)/g);

    return parts.map((part, index) => {
      if (part.startsWith('#')) {
        return (
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/search?q=${encodeURIComponent(part)}&type=all`);
            }}
            className="text-spiders-accent-400 hover:text-spiders-accent-300 hover:underline font-medium transition-colors"
          >
            {part}
          </button>
        );
      } else if (part.startsWith('@')) {
        const mentionedUsername = part.slice(1);
        return (
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/profile/${mentionedUsername}?condition=${condition}`);
            }}
            className="text-spiders-primary-400 hover:text-spiders-primary-300 hover:underline font-medium transition-colors"
          >
            {part}
          </button>
        );
      } else {
        return <span key={index}>{part}</span>;
      }
    });
  };

  const fetchUserProfile = async () => {
    try {
      const { data } = await api.get(
        `/profile/${encodeURIComponent(username)}`
      );
      setProfile(data.user);
      setMessages(data.messages);
      setIsFollowing(data.isFollowing);

      // Fetch additional profile data
      await Promise.all([
        fetchUserReplies(),
        fetchUserLikes(),
        fetchFollowData()
      ]);

      console.log("Messages from API:", data.messages);
    } catch (error) {
      console.error(
        "Error fetching profile:",
        error.response?.data || error.message
      );
      setError("Error fetching profile");
    }
  };

  const fetchUserReplies = async () => {
    try {
      // Fetch all comments by this user
      const { data } = await api.get(
        `/users/${encodeURIComponent(username)}/comments`,
        { params: { condition } }
      );
      setReplies(data || []);
    } catch (error) {
      console.error("Error fetching user replies:", error);
      setReplies([]);
    }
  };

  const fetchUserLikes = async () => {
    try {
      // Fetch all messages liked by this user
      const { data } = await api.get(
        `/users/${encodeURIComponent(username)}/likes`,
        { params: { condition } }
      );
      setLikes(data || []);
    } catch (error) {
      console.error("Error fetching user likes:", error);
      setLikes([]);
    }
  };

  const fetchFollowData = async () => {
    try {
      const [followersData, followingData] = await Promise.all([
        api.get(`/users/${encodeURIComponent(username)}/followers`),
        api.get(`/users/${encodeURIComponent(username)}/following`)
      ]);
      setFollowers(followersData.data);
      setFollowing(followingData.data);
    } catch (error) {
      console.error("Error fetching follow data:", error);
    }
  };

  useEffect(() => {
    fetchUserProfile();
  }, [username, condition]);

  const handleFollowToggle = async () => {
    try {
      if (!user || !user.userId) {
        console.error("User is not logged in");
        return;
      }
      if (profile && profile._id) {
        const endpoint = isFollowing ? "/follow/unfollow" : "/follow";
        const interactionType = isFollowing ? "unfollow" : "follow";

        await api.post(endpoint, { followeeId: profile._id });
        // Follow/unfollow already logged server-side in follow.js

        setIsFollowing(!isFollowing);
        fetchUserProfile();
      } else {
        console.error("Profile or profile ID is missing");
      }
    } catch (error) {
      console.error("Error toggling follow status:", error);
    }
  };

  const handleInteraction = async (type, targetId) => {
    try {
      if (!user || !user.userId) {
        console.error("User is not logged in");
        return;
      }
      // Only call for types NOT already logged server-side
      const serverLoggedTypes = ["like", "unlike", "comment", "follow", "unfollow"];
      if (!serverLoggedTypes.includes(type)) {
        await api.recordInteraction(user.userId, type, targetId);
      }
      fetchUserProfile();
    } catch (error) {
      console.error("Error recording interaction:", error);
    }
  };

  if (error) return <div className="text-spiders-error p-8">{error}</div>;

  if (!profile) {
    return (
      <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
        <div className="flex items-center space-x-3 text-spiders-text-muted">
          <FaSpinner className="animate-spin text-xl" />
          <span>Loading profile...</span>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-spiders-bg">
      {/* Cover Photo */}
      <div className="h-48 relative bg-gradient-to-r from-spiders-primary-700 via-spiders-accent-700 to-spiders-primary-700">
        <div className="absolute inset-0 bg-spiders-bg-dark/20"></div>
      </div>

      {/* Profile Section */}
      <div className="max-w-4xl mx-auto px-4 relative">
        {/* Avatar */}
        <div className="relative -mt-16 mb-4">
          <img
            src={getProfilePicture(profile.username, profile.avatarSeed)}
            alt={profile.username}
            className="w-20 h-20 lg:w-32 lg:h-32 rounded-full bg-spiders-bg border-4 border-spiders-bg"
          />
        </div>

        {/* Profile Info */}
        <div className="pb-4">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <div className="flex items-center flex-wrap gap-2 mb-1">
                <h1 className="text-2xl font-bold text-spiders-text-primary">{profile.username}</h1>
              </div>
              <p className="text-spiders-text-muted text-lg">@{profile.username}</p>
            </div>

            {/* Follow Button */}
            {user && user.username !== profile.username && (
              <button
                onClick={handleFollowToggle}
                className={`flex items-center px-6 py-2 rounded-full font-bold transition-all duration-200 ${
                  isFollowing
                    ? "bg-transparent border-2 border-spiders-error text-spiders-error hover:bg-spiders-error hover:text-white"
                    : "spiders-btn-primary"
                }`}
              >
                {isFollowing ? (
                  <>
                    <FaUserMinus className="mr-2 w-4 h-4" />
                    Unfollow
                  </>
                ) : (
                  <>
                    <FaUserPlus className="mr-2 w-4 h-4" />
                    Follow
                  </>
                )}
              </button>
            )}
          </div>

          {/* Bio */}
          {profile.bio && (
            <p className="text-spiders-text-primary text-base leading-relaxed mb-4 max-w-xl">
              {profile.bio}
            </p>
          )}

          {/* Profile Metadata */}
          <div className="flex flex-wrap items-center space-x-6 text-spiders-text-muted text-sm mb-4">
            <div className="flex items-center space-x-1">
              <FaCalendarAlt className="w-4 h-4" />
              <span>Joined March 2024</span>
            </div>
          </div>

          {/* Stats */}
          <div className="flex space-x-6">
            <button
              onClick={() => setShowFollowingModal(true)}
              className="hover:underline transition-colors group"
            >
              <span className="font-bold text-spiders-text-primary text-base group-hover:text-spiders-primary-400">{following.length}</span>
              <span className="text-spiders-text-muted ml-1">Following</span>
            </button>
            <button
              onClick={() => setShowFollowersModal(true)}
              className="hover:underline transition-colors group"
            >
              <span className="font-bold text-spiders-text-primary text-base group-hover:text-spiders-primary-400">{followers.length}</span>
              <span className="text-spiders-text-muted ml-1">Followers</span>
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-spiders-border">
          <nav className="flex space-x-8 overflow-x-auto hide-scrollbar">
            {[
              { key: "posts", label: "Posts", count: messages.length },
              { key: "replies", label: "Replies", count: replies.length },
              { key: "likes", label: "Likes", count: likes.length }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.key
                    ? "border-spiders-primary-500 text-spiders-text-primary"
                    : "border-transparent text-spiders-text-muted hover:text-spiders-text-secondary"
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className="ml-1 text-xs text-spiders-text-muted">({tab.count})</span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Posts Content */}
        <div className="mt-0">
          {activeTab === "posts" && (
            <div className="space-y-0">
              {messages.length > 0 ? (
                messages.map((message) => (
                  <Message
                    key={message._id}
                    message={message}
                    onInteraction={handleInteraction}
                  />
                ))
              ) : (
                <div className="text-center py-16">
                  <div className="text-spiders-text-muted text-lg mb-2">No posts yet</div>
                  <div className="text-spiders-text-muted text-sm">
                    When {profile.username} posts, they'll show up here.
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "replies" && (
            <div className="space-y-0">
              {replies.length > 0 ? (
                replies.map((comment) => (
                  <div
                    key={comment._id}
                    className="border-b border-spiders-border py-4 px-4 hover:bg-spiders-bg-elevated/30 transition-colors cursor-pointer"
                    onClick={() => {
                      const postId = comment.message?._id;
                      if (postId) {
                        navigate(`/messages/${postId}?condition=${condition}&commentId=${comment._id}`);
                      }
                    }}
                  >
                    {/* Original post preview */}
                    {comment.message?.content && (
                      <div className="mb-2 ml-13 pl-3 border-l-2 border-spiders-border">
                        <div className="flex items-center space-x-1.5 mb-0.5">
                          <img
                            src={getProfilePicture(comment.message?.author?.username || "unknown")}
                            alt={comment.message?.author?.username}
                            className="w-4 h-4 rounded-full"
                          />
                          <span className="text-spiders-text-muted text-xs font-medium">
                            {comment.message?.author?.username || "someone"}
                          </span>
                        </div>
                        <p className="text-spiders-text-muted text-xs line-clamp-2">
                          {comment.message.content}
                        </p>
                      </div>
                    )}
                    <div className="flex space-x-3">
                      <img
                        src={getProfilePicture(profile.username, profile.avatarSeed)}
                        alt={profile.username}
                        className="w-10 h-10 rounded-full border border-spiders-border"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-bold text-spiders-text-primary">{profile.username}</span>
                          <span className="text-spiders-text-muted text-sm">
                            replying to @{comment.message?.author?.username || 'someone'}
                          </span>
                          <span className="text-spiders-text-muted text-sm">
                            {new Date(comment.timestamp).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-spiders-text-primary">{renderTextWithLinks(comment.content)}</p>
                        <div className="flex items-center space-x-4 mt-2 text-spiders-text-muted text-sm">
                          <span>{(comment.likes || []).length} likes</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-16">
                  <div className="text-spiders-text-muted text-lg mb-2">No replies yet</div>
                  <div className="text-spiders-text-muted text-sm">
                    When {profile.username} replies to posts, they'll show up here.
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "likes" && (
            <div className="space-y-0">
              {likes.length > 0 ? (
                likes.map((message) => (
                  <Message
                    key={message._id}
                    message={message}
                    onInteraction={handleInteraction}
                  />
                ))
              ) : (
                <div className="text-center py-16">
                  <div className="text-spiders-text-muted text-lg mb-2">No likes yet</div>
                  <div className="text-spiders-text-muted text-sm">
                    When {profile.username} likes posts, they'll show up here.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showFollowersModal && (
        <UserListModal
          users={followers}
          title="Followers"
          onClose={() => setShowFollowersModal(false)}
        />
      )}
      {showFollowingModal && (
        <UserListModal
          users={following}
          title="Following"
          onClose={() => setShowFollowingModal(false)}
        />
      )}
    </div>
  );
};

export default Profile;
